/**
 * Integração GridRecruter — Painel admin
 * /dp-folha/integracao-gridrecruter
 *
 * Lista assessments externos criados pelo GridRecruter via API REST,
 * mostra status, candidato, empresa externa (tag), link público, histórico
 * completo de tentativas de webhook e botão "Reenviar webhook".
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Plug, RefreshCw, Loader2, Send, Copy, ExternalLink, CheckCircle2, XCircle,
  Clock, Building2, User, Mail, ChevronDown, ChevronRight, AlertCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_BADGE = {
  pending:   'bg-amber-100 text-amber-700 border-amber-300',
  completed: 'bg-green-100 text-green-700 border-green-300',
  expired:   'bg-slate-200 text-slate-600 border-slate-300',
  cancelled: 'bg-slate-200 text-slate-600 border-slate-300',
};
const STATUS_LABEL = {
  pending: 'Pendente', completed: 'Concluído',
  expired: 'Expirado', cancelled: 'Cancelado',
};

// Catálogo (kebab-case) — espelha services/gridrecruter_bridge.py
const TEST_IDS = [
  { id: 'disc-classic',       label: 'DISC' },
  { id: 'big-five',           label: 'Big Five' },
  { id: 'mbti',               label: 'MBTI' },
  { id: 'eneagrama',          label: 'Eneagrama' },
  { id: 'raciocinio-logico',  label: 'Raciocínio Lógico' },
  { id: 'ingles-toeic',       label: 'Inglês — TOEIC' },
  { id: 'excel-avancado',     label: 'Excel Avançado' },
];

function fmtDt(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('pt-BR'); } catch { return s; }
}

function StatusBadge({ status }) {
  return (
    <Badge variant="outline" className={`${STATUS_BADGE[status] || ''} text-xs`}>
      {STATUS_LABEL[status] || status}
    </Badge>
  );
}

function WebhookAttemptRow({ attempt, idx }) {
  const ok = attempt.ok === true;
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <div className="border rounded-md p-2 text-xs bg-white flex items-start gap-2"
         data-testid={`webhook-attempt-${idx}`}>
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${ok ? 'text-green-600' : 'text-red-600'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[11px] text-slate-500">
            {fmtDt(attempt.attempted_at)}
          </span>
          {attempt.http_status !== undefined && (
            <Badge variant="outline"
                   className={`${ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'} text-[10px]`}>
              HTTP {attempt.http_status}
            </Badge>
          )}
          {attempt.skipped && (
            <Badge variant="outline" className="bg-slate-100 text-slate-600 text-[10px]">
              Pulado: {attempt.reason}
            </Badge>
          )}
        </div>
        <div className="text-[11px] text-slate-600 mt-0.5 break-all">
          → {attempt.url || '(sem URL)'}
        </div>
        {attempt.error && (
          <div className="text-[11px] text-red-700 mt-1 break-all">Erro: {attempt.error}</div>
        )}
        {attempt.response_excerpt && (
          <pre className="text-[10px] mt-1 p-1 bg-slate-50 border rounded overflow-x-auto max-h-20">
            {attempt.response_excerpt}
          </pre>
        )}
      </div>
    </div>
  );
}

function AssessmentRow({ a, onResend, busy }) {
  const [expanded, setExpanded] = useState(false);
  const candidate = a.candidate || {};
  const attempts = a.webhook_attempts || [];
  const lastAttempt = attempts[attempts.length - 1];
  const hasOk = attempts.some(t => t.ok);
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <div className="border rounded-lg bg-white" data-testid={`assessment-row-${a.id}`}>
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full text-left p-3 hover:bg-slate-50 flex items-start gap-3"
        data-testid={`assessment-toggle-${a.id}`}
      >
        <Chevron className="h-4 w-4 mt-1 text-slate-400 shrink-0" />
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm flex items-center gap-1">
              <User className="h-3.5 w-3.5 text-slate-400" />
              {candidate.name || '—'}
            </span>
            <Badge variant="outline" className="text-xs">{a.test_name || a.test_id}</Badge>
            <StatusBadge status={a.status} />
            {a.external_company_id && (
              <Badge variant="outline"
                     className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200 flex items-center gap-1"
                     data-testid={`assessment-company-tag-${a.id}`}>
                <Building2 className="h-3 w-3" />
                {a.external_company_name || a.external_company_id}
              </Badge>
            )}
            {attempts.length > 0 && (
              <Badge variant="outline"
                     className={`text-xs ${hasOk ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                {attempts.length} tent. webhook {hasOk ? '✓' : '✗'}
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
            {candidate.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{candidate.email}</span>}
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Criado: {fmtDt(a.created_at)}</span>
            {a.completed_at && <span>Concluído: {fmtDt(a.completed_at)}</span>}
            {a.external_id && <span>ext_id: <code className="text-[10px]">{a.external_id}</code></span>}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t bg-slate-50/50 p-3 space-y-3" data-testid={`assessment-details-${a.id}`}>
          {/* Link público */}
          <div className="space-y-1">
            <div className="text-xs font-medium text-slate-600">Link público do candidato</div>
            <div className="flex items-center gap-2">
              <code className="text-[11px] flex-1 p-1.5 bg-white border rounded break-all">
                {a.public_link}
              </code>
              <Button size="sm" variant="outline"
                      onClick={() => { navigator.clipboard.writeText(a.public_link); toast.success('Link copiado'); }}
                      data-testid={`assessment-copy-link-${a.id}`}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" asChild>
                <a href={a.public_link} target="_blank" rel="noreferrer" data-testid={`assessment-open-link-${a.id}`}>
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </Button>
            </div>
          </div>

          {/* Callback URL */}
          {a.callback_url && (
            <div className="text-xs">
              <span className="font-medium text-slate-600">Callback URL:</span>{' '}
              <code className="text-[11px] break-all">{a.callback_url}</code>
            </div>
          )}

          {/* Score */}
          {a.score !== null && a.score !== undefined && (
            <div className="text-xs">
              <span className="font-medium text-slate-600">Score:</span>{' '}
              <Badge variant="outline">{a.score}</Badge>
            </div>
          )}

          {/* Tentativas de webhook */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-slate-600">
                Histórico de tentativas de webhook ({attempts.length})
              </div>
              <Button
                size="sm"
                variant={hasOk ? 'outline' : 'default'}
                disabled={busy || a.status !== 'completed' || !a.callback_url}
                onClick={() => onResend(a.id)}
                data-testid={`assessment-resend-webhook-${a.id}`}
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                Reenviar webhook
              </Button>
            </div>
            {a.status !== 'completed' && (
              <div className="text-[11px] text-amber-700 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Reenvio só disponível após o candidato concluir o teste.
              </div>
            )}
            {!a.callback_url && (
              <div className="text-[11px] text-slate-500">Sem callback_url — webhook desabilitado.</div>
            )}
            <div className="space-y-1.5">
              {attempts.length === 0 && (
                <div className="text-[11px] text-slate-400 italic">Nenhuma tentativa ainda.</div>
              )}
              {attempts.slice().reverse().map((att, i) => (
                <WebhookAttemptRow key={`${a.id}-att-${i}`} attempt={att} idx={i} />
              ))}
            </div>
            {lastAttempt && !hasOk && a.status === 'completed' && (
              <div className="text-[11px] text-red-700 flex items-center gap-1 mt-1">
                <AlertCircle className="h-3 w-3" />
                Última tentativa falhou — clique em "Reenviar webhook" para tentar novamente.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function GridRecruterIntegracaoPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroTest, setFiltroTest] = useState('todos');
  const [filtroCompany, setFiltroCompany] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filtroStatus !== 'todos') params.status = filtroStatus;
      if (filtroTest !== 'todos') params.test_id = filtroTest;
      if (filtroCompany.trim()) params.external_company_id = filtroCompany.trim();
      const r = await axios.get(`${API}/api/external/assessments`, { ...auth(), params });
      setItems(r.data || []);
    } catch (e) {
      toast.error(`Falha ao carregar assessments: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [filtroStatus, filtroTest, filtroCompany]);

  useEffect(() => { load(); }, [load]);

  const onResend = async (bridgeId) => {
    setBusyId(bridgeId);
    try {
      const r = await axios.post(`${API}/api/external/assessments/${bridgeId}/resend-webhook`,
                                  {}, auth());
      if (r.data?.ok) {
        const tent = r.data.tentativa || {};
        if (tent.ok) toast.success(`Webhook reenviado — HTTP ${tent.http_status}`);
        else toast.error(`Reenvio executado mas resposta não-OK${tent.http_status ? ` (HTTP ${tent.http_status})` : ''}`);
      } else {
        toast.error(`Falha: ${r.data?.erro || 'desconhecida'}`);
      }
      await load();
    } catch (e) {
      toast.error(`Erro: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusyId(null); }
  };

  const filtered = items.filter(a => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (a.candidate?.name || '').toLowerCase().includes(q)
        || (a.candidate?.email || '').toLowerCase().includes(q)
        || (a.external_id || '').toLowerCase().includes(q)
        || (a.id || '').toLowerCase().includes(q);
  });

  const totais = items.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    if ((a.webhook_attempts || []).length > 0 && !a.webhook_attempts.some(t => t.ok)) acc.failed = (acc.failed || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto" data-testid="gridrecruter-integracao-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Plug className="h-7 w-7 text-indigo-600" />
            Integração GridRecruter
            <Badge variant="outline" className="ml-2">Provider · Avaliações</Badge>
          </h1>
          <p className="text-sm text-slate-500">
            Assessments criados externamente via API REST. Inspecione status, callbacks e reenvie webhooks quando necessário.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="gridrecruter-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="border rounded-lg p-3 bg-slate-50" data-testid="kpi-total">
          <div className="text-xs">Total</div><div className="text-2xl font-bold">{items.length}</div>
        </div>
        <div className="border rounded-lg p-3 bg-amber-50" data-testid="kpi-pending">
          <div className="text-xs">Pendentes</div>
          <div className="text-2xl font-bold text-amber-700">{totais.pending || 0}</div>
        </div>
        <div className="border rounded-lg p-3 bg-green-50" data-testid="kpi-completed">
          <div className="text-xs">Concluídos</div>
          <div className="text-2xl font-bold text-green-700">{totais.completed || 0}</div>
        </div>
        <div className="border rounded-lg p-3 bg-red-50" data-testid="kpi-failed">
          <div className="text-xs">Webhook falhou</div>
          <div className="text-2xl font-bold text-red-700">{totais.failed || 0}</div>
        </div>
        <div className="border rounded-lg p-3 bg-slate-50" data-testid="kpi-expired">
          <div className="text-xs">Expirados</div>
          <div className="text-2xl font-bold text-slate-700">{totais.expired || 0}</div>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">Filtros</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Input
              placeholder="Buscar candidato / email / external_id…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-64"
              data-testid="filtro-search"
            />
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-40" data-testid="filtro-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos status</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="completed">Concluído</SelectItem>
                <SelectItem value="expired">Expirado</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filtroTest} onValueChange={setFiltroTest}>
              <SelectTrigger className="w-44" data-testid="filtro-test"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os testes</SelectItem>
                {TEST_IDS.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              placeholder="company_id externo…"
              value={filtroCompany}
              onChange={e => setFiltroCompany(e.target.value)}
              className="w-48"
              data-testid="filtro-company"
            />
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Assessments</CardTitle>
          <CardDescription>
            Clique em qualquer linha para abrir o link público, histórico de webhook e ações administrativas.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <div className="space-y-2">
              {filtered.length === 0 && (
                <div className="text-center text-slate-400 py-8" data-testid="assessments-empty">
                  Nenhum assessment para os filtros selecionados.
                </div>
              )}
              {filtered.map(a => (
                <AssessmentRow
                  key={a.id}
                  a={a}
                  onResend={onResend}
                  busy={busyId === a.id}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
